import { skipToken } from '@reduxjs/toolkit/query';
import { useFetchActiveOnPageSeoAnalysisProcessesQuery } from '../service';
import { useEffect, useState } from 'react';
import { URL_BATCH_ANALYSIS_PROCESS_POLLING_TIME_MS } from '../../settings';
import { deepAnalysisApi } from '../../service';
import { useAppDispatch } from '../../../useAppDispatch';

/**
 * A hook that tracks the status of current analysis processes.
 * @param websiteGuid
 * @param enabled whether to enable fetching and polling
 * @param shouldRefetchOnMount whether the RTK hook should force a refetch on mounts. defaults to **True**
 * @returns A list of ongoing url analysis processes and associated data
 */
export default function useBatchUrlAnalysisProcessesPolling(websiteGuid: string | undefined, enabled: boolean = true, shouldRefetchOnMount: boolean = true) {
    const dispatch = useAppDispatch();
    const [isAnalysisProcessActive, setIsAnalysisProcessActive] = useState<boolean>(false);

    const { data: runningBatchUrlAnalysisProcesses, isLoading: isLoadingRunningProcesses } = useFetchActiveOnPageSeoAnalysisProcessesQuery(
        (enabled && websiteGuid) ? { w_guid: websiteGuid, process_type: 'on_page_seo_batch_url_analysis' } : skipToken,
        {
            pollingInterval: isAnalysisProcessActive ? URL_BATCH_ANALYSIS_PROCESS_POLLING_TIME_MS : 0,
            // If this is set to a number or true it will force refetch on component mount
            refetchOnMountOrArgChange: shouldRefetchOnMount,
        }
    );

    const hasUrlAnalysisProcesses = !!runningBatchUrlAnalysisProcesses && runningBatchUrlAnalysisProcesses.length > 0;
    const hasNoUrlAnalysisProcesses = !!runningBatchUrlAnalysisProcesses && runningBatchUrlAnalysisProcesses.length === 0;

    useEffect(() => {
        // Had no batch process but now there are
        if (hasUrlAnalysisProcesses && isAnalysisProcessActive === false) {
            setIsAnalysisProcessActive(true);
        }

        // Had batch processes but they are finished
        if (hasNoUrlAnalysisProcesses && isAnalysisProcessActive === true) {
            setIsAnalysisProcessActive(false);

            if (websiteGuid) {
                dispatch(
                    deepAnalysisApi.util.invalidateTags([
                        { type: 'UrlAudits', id: websiteGuid },
                        { type: 'ServiceableUrls', id: websiteGuid },
                        { type: 'ServiceableUrlsInCluster', id: websiteGuid },
                        { type: 'AffectedUrls', id: websiteGuid },
                        { type: 'FailedChecks', id: websiteGuid }
                    ])
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, hasNoUrlAnalysisProcesses, hasUrlAnalysisProcesses, websiteGuid]);

    const isRunningBatchAnalysis = !!runningBatchUrlAnalysisProcesses && runningBatchUrlAnalysisProcesses.length > 0;

    return {
        activeBatchUrlAnalysisProcess: runningBatchUrlAnalysisProcesses ? runningBatchUrlAnalysisProcesses[0] : null,
        isRunningBatchAnalysis,
        isLoadingRunningProcesses,
    };
}
